import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {DeleteOutlined, EditOutlined, RestOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import {formatEmptyValue, humanizeBool} from '../../../utils/common'

const UserTable = ({
  dataSource,
  pagination = false,
  getUserListData = null,
  onEdit = null,
  onDelete = null,
  showDeleted = false,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'users.email' }),
        dataIndex: 'email',
        width: '25%',
        sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'users.first_name' }),
        dataIndex: 'first_name',
        width: '20%',
        sorter: (a, b) => (a.first_name || '').localeCompare(b.first_name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'users.last_name' }),
        dataIndex: 'last_name',
        width: '20%',
        sorter: (a, b) => (a.last_name || '').localeCompare(b.last_name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'users.isAdmin' }),
        dataIndex: 'is_admin',
        width: '10%',
        sorter: (a, b) => (a.is_admin || false) === (b.is_admin || false),
        render: (text) => humanizeBool(text),
      },
      {
        title: intl.formatMessage({ id: 'users.isActive' }),
        dataIndex: 'is_active',
        width: '10%',
        sorter: (a, b) => (a.is_active || false) === (b.is_active || false),
        render: (text) => humanizeBool(text),
      },
      {
        title: intl.formatMessage({ id: 'users.action' }),
        align: 'right',
        width: 135,
        render: (_, record) => (
          <TableActions
            actions={[
              ...(record?.is_active
                ? [
                    {
                      key: 'edit',
                      label: intl.formatMessage({ id: 'forms.edit' }),
                      icon: <EditOutlined />,
                      onClick: () => onEdit(record),
                    },
                  ]
                : []),
              showDeleted
                ? {
                    key: 'restore',
                    label: intl.formatMessage({ id: 'users.restore' }),
                    icon: <RestOutlined />,
                    onClick: () => onDelete(record),
                  }
                : {
                    key: 'delete',
                    label: intl.formatMessage({ id: 'users.delete' }),
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => onDelete(record),
                  },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination, showDeleted]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `user-${record.email}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={{
          y: '70vh',
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getUserListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default UserTable
